/* eslint-disable @typescript-eslint/no-empty-function */
import { memo, useCallback, useMemo, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { getGoogleMapsAPIKey } from 'src/services/utils/apiHelpers';
import { useTranslation } from 'react-i18next';
import { Icon } from 'src/components/Icon';
import { Button } from 'src/components/forms';
import { DealerLocationSearchProps, SelectedLocation } from './DealerInterface';
import classNames from 'classnames';
import { trackWidgetSearchCity } from 'src/utils/tracking/trackingSlice';
import { useAppDispatch } from 'src/stores';
import { inputValue, updateQueryParams } from '../utils/dealerSearchHelper';
import { IsCommercial } from 'src/utils/variables';
import { resetUserLocation } from '../utils/dealerSlice';

const { config } = window || {};
const { Site, GMaps, country } = config || {};
const shopType = Site?.shopType;

const apikey = getGoogleMapsAPIKey();
const countries =
  shopType === 'NON_SHOP' || shopType === 'NON_SHOP_WITH_PRICES'
    ? GMaps?.countries
    : [country];

export const DealerLocationSearch = memo(
  ({
    location,
    setLocation,
    fetchUserLocation = () => {},
    onClearClick,
    isDealerSearchActivator = false,
    isAppointmentBooking = false,
    setIsInit,
    isSeoPage,
    dealersCount,
    dealerSearchUrl,
    promotionIdPresent,
    name,
    error,
  }: DealerLocationSearchProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [menuOpen, SetMenu] = useState(false);

    const translationsArray = [
      t('dealersearch.dealerlist.storesnear-0'),
      t('dealersearch.dealerlist.storesnear-1'),
      t('dealersearch.dealerlist.storesnear-2'),
      t('dealersearch.dealerlist.storesnear-more'),
      t('dealersearch.dealerlist.storesnear-0.promotions'),
    ];

    const DropdownIndicator = useCallback(
      () =>
        location?.value.place_id &&
        (!isDealerSearchActivator || !promotionIdPresent) ? null : (
          <Icon
            iconName="Search"
            height={24}
            width={24}
            className={classNames('text-brand-primary mr-2', {
              'text-dark-gray': IsCommercial,
            })}
          />
        ),
      [location?.value.place_id, isDealerSearchActivator, promotionIdPresent]
    );

    const handleOnClear = useCallback(() => {
      dispatch(resetUserLocation());
      updateQueryParams({
        place: '',
        placeId: '',
      });
      setLocation({
        label: '',
        value: {
          place_id: '',
        },
      });
      if (onClearClick) {
        onClearClick();
      }
    }, [setLocation, onClearClick]);

    const ClearIndicator = useCallback(
      ({ innerRef }: any) =>
        location?.value.place_id && !isDealerSearchActivator ? (
          <div
            ref={innerRef}
            onTouchStart={handleOnClear}
            onClick={handleOnClear}
          >
            <Icon
              iconName="CloseDealerSearch"
              className={`mr-3 cursor-pointer ${
                isAppointmentBooking ? 'text-white' : 'text-brand-primary'
              }`}
            />
          </div>
        ) : null,
      [
        location?.value.place_id,
        isDealerSearchActivator,
        handleOnClear,
        isAppointmentBooking,
      ]
    );

    const CustomMenuList = useCallback(
      ({ innerRef, innerProps, children }: any) => (
        <div
          ref={innerRef}
          {...innerProps}
          data-testid="dealer-location-search-menu-list"
        >
          {!isDealerSearchActivator && (
            <div className="flex flex-row my-2">
              <div className="basis-auto text-left text-brand-primary ml-2">
                <Icon iconName="MyLocation" className="inline" />
              </div>
              <div>
                <Button
                  className={`align-middle pl-1 !text-brand-primary`}
                  onClick={() => {
                    fetchUserLocation();
                    SetMenu(false);
                  }}
                  variant="link"
                  inverse
                >
                  {t('dealersearch.label.use-current-location')}
                </Button>
              </div>
            </div>
          )}
          {children}
        </div>
      ),
      [fetchUserLocation, isDealerSearchActivator]
    );

    const handleChange = useCallback(
      (selectedLocation: SelectedLocation) => {
        dispatch(trackWidgetSearchCity());
        if (typeof setIsInit === 'function') setIsInit(true);
        setLocation(selectedLocation);
        if (isSeoPage && dealerSearchUrl)
          window.location.href = dealerSearchUrl;
      },
      [dispatch, setIsInit, setLocation, isSeoPage, dealerSearchUrl]
    );

    const inputValueMemorized = useMemo(
      () =>
        inputValue(
          location,
          translationsArray,
          dealersCount,
          promotionIdPresent
        ),
      [location, dealersCount]
    );

    return (
      <>
        <GooglePlacesAutocomplete
          apiKey={apikey}
          selectProps={{
            name: name,
            label: 'Test',
            menuIsOpen: menuOpen,
            onMenuOpen: () => SetMenu(true),
            onMenuClose: () => SetMenu(false),
            value: location?.value?.place_id
              ? {
                  ...inputValueMemorized,
                  label: (
                    <div
                      className={classNames({ 'text-body': !IsCommercial })}
                      dangerouslySetInnerHTML={{
                        __html:
                          !menuOpen && inputValueMemorized.label
                            ? inputValueMemorized.label
                            : t('dealersearch-search-field.label.hint'),
                      }}
                    />
                  ),
                }
              : t('dealersearch-search-field.label.hint'),
            onChange: handleChange,
            defaultValue: isSeoPage ? inputValueMemorized : location,
            placeholder: t('dealersearch-search-field.label.hint'),
            isClearable: true,
            styles: {
              indicatorSeparator: (base: Record<string, string>) => ({
                ...base,
                display: 'none',
                marginLeft: '2rem',
              }),
              input: (base: Record<string, string>) => ({
                ...base,
              }),
              placeholder: (base: Record<string, string>) => ({
                ...base,
              }),
              clearIndicator: (base: Record<string, string>) => ({
                ...base,
                marginRight: '2rem',
              }),
              noOptionsMessage: (base: Record<string, string>) => ({
                ...base,
                display: 'none',
              }),
              menu: (provided: Record<string, string>) => ({
                ...provided,
                marginTop: '0px',
                borderTopStyle: 'none',
                borderRadius: '0px',
                zIndex: '2',
              }),
              control: (provided: Record<string, string>, state: any) => ({
                ...provided,
                padding: promotionIdPresent ? '1px 10px 1px 2px' : '0',
                borderRadius: 'var(--brand-radius)',
                boxShadow: 'none',
                borderColor: error ? 'rgb(var(--error))' : '#979797',

                borderWidth: error ? '2px' : '1px',
                '&:hover': {
                  borderColor: 'none',
                },
                backgroundColor:
                  location && isAppointmentBooking && !state.menuIsOpen
                    ? 'rgb(var(--brand-primary))'
                    : 'white',
                fontSize: '16px',
              }),
              option: (provided: Record<string, string>) => ({
                ...provided,
                color: 'rgb(var(--brand-primary))',
                fontWeight: '700',
              }),
              singleValue: (provided: Record<string, string>) => ({
                ...provided,
                color: menuOpen
                  ? '#808080'
                  : !isAppointmentBooking
                  ? '#333333'
                  : menuOpen
                  ? '#333333'
                  : 'white',
              }),
            },
            components: {
              DropdownIndicator,
              ClearIndicator,
              MenuList: CustomMenuList,
            },
            theme: (theme: any) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: 'rgb(var(--wild-sand))',
                primary: 'rgb(var(--wild-sand))',
              },
            }),
          }}
          autocompletionRequest={{
            componentRestrictions: {
              country: countries,
            },
          }}
        />
        {error && <div className="text-error mt-1 leading-6"> {error} </div>}
      </>
    );
  }
);
